import { useEffect, useState } from 'react';
import { ArrowRight, Loader2, Package } from 'lucide-react';

import { transferStock } from '../api/warehouses';

/**
 * Bottom-sheet form for moving a stock batch between warehouses.
 *
 * Mirrors RevertSaleSheet's UX vocabulary — slide-up sheet, backdrop
 * dismiss, escape close — so the PWA's "structured action over a
 * source row" pattern stays consistent across pages.
 *
 * Props:
 *   row              — the WarehouseStockItem the rep tapped. Provides
 *                       batch_id, product_name, order_number,
 *                       quantity_available.
 *   sourceWarehouse  — the Warehouse object the row belongs to (so we
 *                       can show its name and lock from_warehouse_id).
 *   warehouses       — full warehouse list (used to populate the
 *                       destination picker, with the source filtered
 *                       out).
 *   onClose          — close without transferring.
 *   onSuccess        — fired after a successful transfer; the parent
 *                       refreshes its stock view and shows a flash.
 *
 * Backend: POST /warehouses/transfer (auth: get_current_user). Schema
 * already enforces source ≠ destination, but we also filter the source
 * out of the picker so the rep never picks an invalid combo.
 */
export default function TransferStockSheet({
  row,
  sourceWarehouse,
  warehouses,
  onClose,
  onSuccess,
}) {
  // Destination — preselect the first warehouse that's NOT the source,
  // so a one-warehouse-pair transfer is one tap less.
  const destinations = (warehouses || []).filter(
    (w) => w.id !== sourceWarehouse?.id,
  );
  const [toWarehouseId, setToWarehouseId] = useState(
    destinations[0]?.id ?? null,
  );

  // Quantity — default to 1, capped client-side at the source's
  // available qty. Backend validates again so this is just for UX.
  const [quantity, setQuantity] = useState('1');
  const [reason, setReason] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Close on Escape so keyboard QA can back out cleanly.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const available = Number(row?.quantity_available ?? 0);
  const qty = Number(quantity) || 0;
  const canSubmit =
    !submitting &&
    toWarehouseId != null &&
    qty > 0 &&
    qty <= available;

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await transferStock({
        batchId:          row.batch_id,
        fromWarehouseId:  sourceWarehouse.id,
        toWarehouseId,
        quantity:         qty,
        reason:           reason || undefined,
      });
      onSuccess({
        productName:    row.product_name,
        quantity:       qty,
        toWarehouseId,
        toWarehouseName: destinations.find((w) => w.id === toWarehouseId)?.name,
      });
    } catch (err) {
      setError(
        err.response?.data?.detail || err.message || 'Transfer failed',
      );
      setSubmitting(false);
    }
    // Note: no setSubmitting(false) on success — parent unmounts the
    // sheet via onSuccess.
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="transfer-title"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white rounded-t-sheet shadow-2xl px-4 pt-3 pb-safe-bottom max-h-[92vh] overflow-y-auto"
        style={{ animation: 'sheet-slide-up 180ms ease-out' }}
      >
        <div className="w-10 h-1 bg-gray-300 rounded-pill mx-auto mb-2" />

        <h2 id="transfer-title" className="text-title font-bold text-gray-900 mb-1">
          Transfer stock
        </h2>

        {/* Source card — identifies what's being moved. Gray surface
            (read-only feel) plus the available qty so the rep
            doesn't have to remember it. */}
        <div className="bg-gray-50 border border-gray-200 rounded-card p-3 mb-3 flex items-start gap-3">
          <Package className="h-5 w-5 text-gray-700 shrink-0 mt-0.5" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <p className="text-body font-medium text-gray-900 truncate">
              {row?.product_name ?? `Batch ${row?.batch_id}`}
            </p>
            <p className="text-caption text-gray-600">
              {sourceWarehouse?.name}
              {row?.order_number && <> · {row.order_number}</>}
            </p>
            <p className="text-caption text-gray-500 mt-0.5">
              {available} available
            </p>
          </div>
        </div>

        {/* Destination picker — chips when small set, dropdown when
            many. Source warehouse is filtered out so an invalid pair
            isn't selectable. */}
        <label className="block text-small font-medium text-gray-700 mb-2">
          Move to *
        </label>
        {destinations.length === 0 ? (
          <p className="text-small text-gray-500 mb-3">
            No other warehouse to transfer to. Set up another in the desktop ERP.
          </p>
        ) : destinations.length <= 4 ? (
          <div className="grid grid-cols-2 gap-2 mb-3">
            {destinations.map((w) => (
              <button
                key={w.id}
                type="button"
                onClick={() => setToWarehouseId(w.id)}
                className={[
                  'p-3 rounded-card border-2 text-left text-small font-semibold transition-colors flex items-center gap-2',
                  toWarehouseId === w.id
                    ? 'border-amber-500 bg-amber-50 text-amber-900'
                    : 'border-gray-200 text-gray-700',
                ].join(' ')}
              >
                <ArrowRight className="h-4 w-4 shrink-0" />
                <span className="truncate">{w.name}</span>
              </button>
            ))}
          </div>
        ) : (
          <select
            value={toWarehouseId ?? ''}
            onChange={(e) => setToWarehouseId(Number(e.target.value))}
            className="w-full min-h-touch px-3 mb-3 border border-gray-300 rounded-card text-body bg-surface focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
          >
            {destinations.map((w) => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
        )}

        {/* Quantity — number input clamped at the source's available qty. */}
        <label className="block text-small font-medium text-gray-700 mb-1">
          Quantity *
        </label>
        <input
          type="number"
          inputMode="numeric"
          min="1"
          max={available}
          step="1"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          className="w-full min-h-touch px-3 mb-1 border border-gray-300 rounded-card text-body focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
        />
        {qty > available && (
          <p className="text-caption text-danger mb-2">
            Only {available} available at the source.
          </p>
        )}
        {qty > 0 && qty <= available && (
          <p className="text-caption text-gray-500 mb-3">
            Source will be left with {available - qty}.
          </p>
        )}
        {!qty && <div className="mb-3" />}

        {/* Optional reason — useful for the audit trail. */}
        <label className="block text-small font-medium text-gray-700 mb-1">
          Reason (optional)
        </label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          placeholder="e.g. Restocking Harare for a customer pickup"
          className="w-full px-3 py-2 mb-3 border border-gray-300 rounded-card text-body focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
        />

        {error && (
          <p role="alert" className="text-small text-danger mb-3">
            {error}
          </p>
        )}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 min-h-touch rounded-card border border-gray-300 bg-white text-gray-700 font-medium disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="flex-1 min-h-touch rounded-card font-semibold text-white text-body bg-amber-500 active:bg-amber-600 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
          >
            {submitting && <Loader2 size={18} className="animate-spin" />}
            {submitting ? 'Transferring…' : 'Transfer'}
          </button>
        </div>

        <style>{`
          @keyframes sheet-slide-up {
            from { transform: translateY(100%); }
            to   { transform: translateY(0); }
          }
        `}</style>
      </div>
    </div>
  );
}
